import React from 'react';
import { useLocalStorage, useLocation } from 'react-use';
import createAuth0Client from '@auth0/auth0-spa-js';
import { Trans, useTranslation } from 'react-i18next';
import {
    createStyles,
    makeStyles,
    Theme,
} from '@bb-ui/react-library/dist/components/styles';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { LeaveSession } from '@bb-ui/icons/dist/small/LeaveSession';
import { ButtonBase } from '@bb-ui/react-library/dist/components/ButtonBase';
import { useHistory } from 'react-router-dom';
import {
    AuthProviderCacheProps,
    useAuthProviderCache,
} from './useAuthProviderCache';
import { Auth0CallbackHandler } from './Auth0CallbackHandler';
import { UASCallbackHandler } from './UASCallbackHandler';
import { Auth0Context, Auth0Provider } from './Auth0Context';
import { useAppLoadingContext } from '../../contexts/AppLoadingContext';
import { Message } from '../../components/Message/Message';
import { localStorageUASTenantKey } from '../../utilities/constants';
import { xconsole } from '../../utilities/utilities';
import {
    AccountNotProvisionedDetail,
    parseAuthFailureDetail,
} from './authFailureDetail';
import { AccountNotProvisioned } from './AccountNotProvisioned';

export interface Auth0CallbackProps {
    onRedirect: (path: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        errorContainer: {
            background: theme.palette.background.b3,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
        },
        button: {
            marginTop: theme.spacing(2),
        },
        buttonText: {
            marginInlineEnd: '8px',
        },
    }),
);

export const AuthHandlingWrapper: React.FC = props => {
    const { cache, setCache } = useAuthProviderCache();
    const { setLoadingFlag } = useAppLoadingContext();
    const [authCallbackErrorMessage, setAuthCallbackErrorMessage] =
        React.useState<string | undefined>();
    const [accountNotProvisioned, setAccountNotProvisioned] = React.useState<
        AccountNotProvisionedDetail | undefined
    >();
    const history = useHistory();
    const classes = useStyles();

    const { t } = useTranslation();
    const location = useLocation();

    const initiateLoginWithRedirect = React.useCallback(
        async (props: AuthProviderCacheProps) => {
            setLoadingFlag('app.redirecting', true);
            const auth0Client = await createAuth0Client({
                ...props,
                redirect_uri: `${window.location.origin}/callback`,
            });
            setCache(props);

            auth0Client.loginWithRedirect({
                audience: props.audience,
                appState: { returnPath: window.location.pathname },
            });
        },
        [setCache, setLoadingFlag],
    );

    const [, setUasTenant] = useLocalStorage<string>(
        localStorageUASTenantKey,
        '',
        {
            raw: true,
        },
    );

    // Either failure screen counts. Guarding on the generic message alone left the
    // sign-out control inert on the account-not-provisioned branch, which -- with no
    // timer on that branch -- made the screen a dead end.
    const hasSignInFailure = Boolean(
        authCallbackErrorMessage || accountNotProvisioned,
    );

    const redirectToLogout = React.useCallback(async () => {
        if (cache && hasSignInFailure) {
            const auth0Client = await createAuth0Client(cache);
            setCache(undefined);
            setUasTenant('');
            auth0Client.logout({ returnTo: window.location.href });
        }
    }, [cache, hasSignInFailure, setCache, setUasTenant]);

    // Only the generic branch carries this time limit. The account-not-provisioned
    // branch sets no timer, so the user can read and relay the detail. The limit left
    // here is a known accessibility failure on the generic path, tracked separately.
    React.useEffect(() => {
        if (authCallbackErrorMessage) {
            setTimeout(redirectToLogout, 15000);
        }
    }, [authCallbackErrorMessage, redirectToLogout]);

    const renderLogoutControl = (opts?: {
        className?: string;
        label?: string;
    }) => {
        // The account-not-provisioned screen names where the control leads instead of
        // saying "Sign Out" under "We couldn't sign you in". auth.logout is unchanged
        // for the header and sidebar, where the user really is signed in.
        const label = opts?.label ?? t('auth.logout');

        return (
            cache && (
                <ButtonBase
                    className={opts?.className}
                    onClick={redirectToLogout}
                    aria-label={label}
                >
                    <Typography
                        variant="subtitle2"
                        component="span"
                        className={classes.buttonText}
                    >
                        {label}
                    </Typography>
                    <LeaveSession />
                </ButtonBase>
            )
        );
    };

    if (accountNotProvisioned) {
        return (
            <div className={classes.errorContainer}>
                <AccountNotProvisioned detail={accountNotProvisioned}>
                    {renderLogoutControl({
                        label: t('general.accountNotProvisionedBackHome'),
                    })}
                </AccountNotProvisioned>
            </div>
        );
    }

    if (authCallbackErrorMessage) {
        return (
            <div className={classes.errorContainer}>
                <Message
                    variant="error"
                    message={
                        <Trans i18nKey="general.applicationLoadError">
                            The <span lang="en">Blackboard Illuminate</span>{' '}
                            application didn't load.
                        </Trans>
                    }
                    secondaryMessage={authCallbackErrorMessage}
                >
                    {renderLogoutControl({ className: classes.button })}
                </Message>
            </div>
        );
    }

    if (location.pathname === '/sign-in-callback') {
        return <UASCallbackHandler />;
    }

    if (location.pathname === '/callback') {
        if (cache === undefined) {
            history.push('/');
            throw new Error('Invalid login attempt - no authContextData found');
        }
        const error = new URLSearchParams(window.location.search).get('error');
        if (error) {
            const rawErrorDescription = new URLSearchParams(
                window.location.search,
            ).get('error_description');
            const errorDescription =
                rawErrorDescription || t('general.pleaseRefreshThePage');
            xconsole.error(
                `Authentication callback returned an error -> ${error}, `,
            );
            const failureDetail = parseAuthFailureDetail(
                rawErrorDescription ?? undefined,
            );
            setAccountNotProvisioned(failureDetail);
            setAuthCallbackErrorMessage(
                failureDetail ? undefined : errorDescription,
            );
            // there should be no flag set up at this point
            // but the default state is that the loading indicator is shown
            // before any flags appear; so this should force it to disappear again
            setLoadingFlag('auth.init', false);
            // replace, not push, so the callback URL — which carries the username in
            // its query string — does not stay reachable via Back. Partial only: the
            // browser committed it to persistent history before any script ran.
            history.replace('/');
            return <></>;
        }
    }

    if (cache === undefined) {
        return (
            <Auth0Context.Provider
                value={{
                    isAuthenticated: false,
                    loading: false,
                    initiateLoginWithRedirect,
                }}
            >
                {props.children}
            </Auth0Context.Provider>
        );
    }
    return (
        <Auth0Provider
            {...cache}
            redirect_uri={`${window.location.origin}/callback`}
            initiateLoginWithRedirect={initiateLoginWithRedirect}
        >
            {location.pathname === '/callback' ? (
                // at this moment silent auth should work
                // having it here makes it possible not to have to
                // auth twice, as the context stays alive after the redirect
                <Auth0CallbackHandler />
            ) : (
                // the actual content of the page
                // -> not rendered when path is /callback, not to re-render
                // the page before silent auth happens
                props.children
            )}
        </Auth0Provider>
    );
};
